import React, { Suspense } from 'react';
import styled from 'styled-components';
import { PortableText } from '@portabletext/react';
import type { TypedObject } from '@portabletext/types';

import BlockBuilderImage from '@repo/ui-components/sanity/types/BlockBuilderImage';
import VideoGallery from '@repo/ui-components/sanity/types/VideoGallery';
import Button from '@repo/ui-components/button/Button';
import sharedStyles from '@repo/ui-components/styles';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import type { SanityBlockBuilderAccordionItem } from '@repo/models/gatsby-graphql-types';

const Accordion = React.lazy(() =>
  import('react-accessible-accordion').then(module => ({
    default: module.Accordion,
  }))
);

const AccordionItem = React.lazy(() =>
  import('react-accessible-accordion').then(module => ({
    default: module.AccordionItem,
  }))
);

const AccordionItemHeading = React.lazy(() =>
  import('react-accessible-accordion').then(module => ({
    default: module.AccordionItemHeading,
  }))
);

const AccordionItemButton = React.lazy(() =>
  import('react-accessible-accordion').then(module => ({
    default: module.AccordionItemButton,
  }))
);

const AccordionItemPanel = React.lazy(() =>
  import('react-accessible-accordion').then(module => ({
    default: module.AccordionItemPanel,
  }))
);

const isSSR = globalThis.window === undefined;

const Container = styled.div`
  margin-block: ${sharedStyles.spacing['spacing-2xl']};

  [hidden] {
    display: none;
  }

  .accordion {
    border: none;
  }

  .accordion__heading {
    margin: 0;
  }

  .accordion__button {
    padding: ${sharedStyles.spacing['spacing-md']};
    font-weight: bolder;
    /*fontWeight.headline*/
    display: flex;
    align-items: center;
    gap: ${sharedStyles.spacing['spacing-md']};
    color: ${({ theme }) => theme.colors.accordion.color};
    justify-content: space-between;
    background-color: ${({ theme }) => theme.colors.accordion.background};
    cursor: pointer;
    width: 100%;
    text-align: left;
    border: none;

    &:before {
      display: inline-block;
      content: '';
      height: 10px;
      width: 10px;
      border-bottom: 2px solid currentColor;
      border-right: 2px solid currentColor;
      margin: 0;
      order: 1;
      align-self: center;
      transform: rotate(45deg);
    }

    &[aria-expanded='true']::before,
    &[aria-selected='true']::before {
      transform: rotate(225deg);
    }

    h3 {
      flex-grow: 1;
      margin: 0;
    }
  }

  .accordion__button:hover {
    background-color: ${({ theme }) => theme.colors.accordion.backgroundHover};
  }

  .accordion__panel {
    padding: ${sharedStyles.spacing['spacing-md']};
    animation: fadein 0.35s ease-in;

    img {
      margin: 0 0 ${sharedStyles.spacing['spacing-md']};
    }
  }

  .accordion__item + .accordion__item {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  @keyframes fadein {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;

interface SupportArticleAccordionProps {
  section: {
    headline?: string;
    items?: SanityBlockBuilderAccordionItem[];
  };
}

function SupportArticleAccordion({
  section,
}: Readonly<SupportArticleAccordionProps>) {
  const { tt } = useUiComponentsInternationalContext();

  return (
    <Container>
      {section?.headline && <h2>{section.headline}</h2>}

      {!isSSR && (
        <Suspense
          fallback={<div>{tt(s => s.fallbackCommon.loadingFallback)}</div>}
        >
          <Accordion allowZeroExpanded allowMultipleExpanded>
            {section?.items?.length > 0 &&
              section.items.map(item => (
                <AccordionItem key={item._key}>
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      <h3>{item?.headline}</h3>
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    {item?.media?.asset && (
                      <BlockBuilderImage value={item.media} />
                    )}
                    {item?.video && <VideoGallery value={item.video} />}
                    {item?.copy?.text && (
                      <PortableText value={item.copy.text as TypedObject[]} />
                    )}
                    {item?.cta?.linkType === 'internal' && (
                      <Button to={item?.cta?.internalLink} importance="primary">
                        {item?.cta?.caption}
                      </Button>
                    )}
                    {item?.cta?.linkType === 'external' && (
                      <Button
                        href={item?.cta?.href}
                        target={item?.cta?.blank ? '_blank' : '_self'}
                        rel="noreferrer"
                        importance="primary"
                      >
                        {item?.cta?.caption}
                      </Button>
                    )}
                  </AccordionItemPanel>
                </AccordionItem>
              ))}
          </Accordion>
        </Suspense>
      )}
    </Container>
  );
}

export default SupportArticleAccordion;
